import React from 'react';
import {
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th as ChakraTh,
  Td as ChakraTd,
  Tfoot,
  Container,
  Text,
  Flex,
} from '@chakra-ui/react';
import { RichText } from '../RichText';

const TdIndent = ({ children, ...restProps }) => (
  <ChakraTd
    fontWeight={300}
    paddingLeft={50}
    {...restProps}
    borderBottom='1px solid'
    borderColor='zinc.200'
  >
    {children}
  </ChakraTd>
);

const TdBold = ({ children, ...restProps }) => (
  <ChakraTd
    fontWeight={600}
    fontSize={{ base: 'sm', xl: 'md' }}
    {...restProps}
    borderBottom='1px solid'
    borderColor='zinc.200'
  >
    {children}
  </ChakraTd>
);

const Td = ({ children, ...restProps }) => (
  <ChakraTd
    fontWeight={300}
    fontSize={{ base: 'sm', xl: 'md' }}
    {...restProps}
    borderBottom='1px solid'
    borderColor='zinc.200'
  >
    {children}
  </ChakraTd>
);

const ThFooter = ({ children, ...restProps }) => (
  <ChakraTh
    fontWeight={300}
    fontSize={{ base: 'sm', xl: 'md' }}
    {...restProps}
    padding={10}
    color='black'
    textTransform='none'
  >
    {children}
  </ChakraTh>
);

const Th = ({ children, ...restProps }) => (
  <ChakraTh fontWeight={600} fontSize='xs' padding={10} color='black' {...restProps}>
    {children}
  </ChakraTh>
);

const BudgetTable = ({ data }) => {
  const { title, subtitle, tableNumber, source } = data;

  return (
    <Container maxW='container.xl' padding={{ base: 30, xl: 60 }}>
      <Flex
        flexDirection={{ base: 'column', xl: 'row' }}
        gap={{ base: 0, xl: 30 }}
        p={20}
        background='zinc.100'
      >
        <Flex flexDirection='column' mb={20} alignItems='flex-start' spacing='2px' flex='1'>
          {tableNumber && (
            <Text fontSize='xs' textTransform='uppercase' fontWeight={500} letterSpacing={1}>
              {tableNumber}
            </Text>
          )}
          {title && (
            <Text as='h2' textStyle='h4' fontWeight={600}>
              {title}
            </Text>
          )}
          {subtitle && (
            <RichText value={subtitle} fontSize='14px' fontWeight={500} lineHeight={1.1} />
          )}
        </Flex>
        <TableContainer flex='4'>
          <Table variant='simple' size='sm'>
            <Thead>
              <Tr>
                <Th borderBottom='1px solid black' />
                <Th borderBottom='1px solid black'>FY23 Total Budget</Th>
                <Th borderBottom='1px solid black'>FY23 Outturn</Th>
                <Th borderBottom='1px solid black'>FY24 Structural</Th>
                <Th borderBottom='1px solid black'>FY24 Temporary</Th>
                <Th borderBottom='1px solid black'>FY24 Total Budget</Th>
                <Th borderBottom='1px solid black'>FY24 Outturn</Th>
                <Th borderBottom='1px solid black'>FY25 Total Budget</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <TdBold>Gross Fund Financed</TdBold>
                <TdBold>1,432</TdBold>
                <TdBold>1,327</TdBold>
                <TdBold>1,455</TdBold>
                <TdBold>95</TdBold>
                <TdBold>1,551</TdBold>
                <TdBold>1,450</TdBold>
                <TdBold>1,642</TdBold>
              </Tr>
              <Tr>
                <TdIndent>Net administrative budget</TdIndent>
                <Td>1,295</Td>
                <Td>1,294</Td>
                <Td>1,411</Td>
                <Td>-</Td>
                <Td>1,411</Td>
                <Td>1,410</Td>
                <Td>1,501</Td>
              </Tr>
              <Tr>
                <TdIndent>o/w FY augmentation</TdIndent>
                <Td>23</Td>
                <Td>-</Td>
                <Td>29</Td>
                <Td>-</Td>
                <Td>29</Td>
                <Td>-</Td>
                <Td>30</Td>
              </Tr>
              <Tr>
                <TdIndent>o/w OED budget increase</TdIndent>
                <Td>-</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>9</Td>
              </Tr>
              <Tr>
                <TdIndent>o/w Annual Meetings</TdIndent>
                <Td>-</Td>
                <Td>-</Td>
                <Td>7</Td>
                <Td>-</Td>
                <Td>7</Td>
                <Td>7</Td>
                <Td>-</Td>
              </Tr>
              <Tr>
                <TdIndent>General Receipts¹</TdIndent>
                <Td>36</Td>
                <Td>33</Td>
                <Td>44</Td>
                <Td>-1</Td>
                <Td>44</Td>
                <Td>40</Td>
                <Td>48</Td>
              </Tr>
              <Tr>
                <TdIndent>Carryforward and Other</TdIndent>
                <Td>102</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>96</Td>
                <Td>96</Td>
                <Td>-</Td>
                <Td>94</Td>
              </Tr>
              <Tr>
                <TdBold>Gross Externally Financed²</TdBold>
                <TdBold>236</TdBold>
                <TdBold>195</TdBold>
                <TdBold>250</TdBold>
                <TdBold>7</TdBold>
                <TdBold>257</TdBold>
                <TdBold>216</TdBold>
                <TdBold>283</TdBold>
              </Tr>
              <Tr>
                <TdIndent>Receipts (largely CD-related)</TdIndent>
                <Td>230</Td>
                <Td>195</Td>
                <Td>250</Td>
                <Td>-</Td>
                <Td>250</Td>
                <Td>219</Td>
                <Td>276</Td>
              </Tr>
              <Tr>
                <TdIndent>Carryforward (limit)</TdIndent>
                <Td>6</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>7</Td>
                <Td>7</Td>
                <Td>-</Td>
                <Td>8</Td>
              </Tr>
              <Tr>
                <TdBold>Gross Administrative Envelope</TdBold>
                <TdBold>1,668</TdBold>
                <TdBold>1,522</TdBold>
                <TdBold>1,706</TdBold>
                <TdBold>102</TdBold>
                <TdBold>1,808</TdBold>
                <TdBold>1,666</TdBold>
                <TdBold>1,925</TdBold>
              </Tr>
              <Tr>
                <TdBold>Capital³</TdBold>
                <TdBold>78</TdBold>
                <TdBold>96</TdBold>
                <TdBold>108</TdBold>
                <TdBold>-</TdBold>
                <TdBold>108</TdBold>
                <TdBold>110</TdBold>
                <TdBold>122</TdBold>
              </Tr>
              <Tr>
                <TdIndent>Facilities</TdIndent>
                <Td>19</Td>
                <Td>38</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>47</Td>
                <Td>49</Td>
                <Td>54</Td>
              </Tr>
              <Tr>
                <TdIndent>IT Intensive - Direct</TdIndent>
                <Td>44</Td>
                <Td>45</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>41</Td>
                <Td>42</Td>
                <Td>45</Td>
              </Tr>
              <Tr>
                <TdIndent>IT Intensive - Cloud Capital Equivalent</TdIndent>
                <Td>15</Td>
                <Td>13</Td>
                <Td>-</Td>
                <Td>-</Td>
                <Td>20</Td>
                <Td>19</Td>
                <Td>23</Td>
              </Tr>
            </Tbody>
            <Tfoot borderTop='2px solid black'>
              <Tr>
                <ThFooter>Carryforward</ThFooter>
                <TdBold>93</TdBold>
                <TdBold>-</TdBold>
                <TdBold>-</TdBold>
                <TdBold>87</TdBold>
                <TdBold>87</TdBold>
                <TdBold>-</TdBold>
                <TdBold>79</TdBold>
              </Tr>
            </Tfoot>
          </Table>
        </TableContainer>
      </Flex>
      {source && (
        <RichText value={source} fontSize='xs' fontWeight={300} mt={10} lineHeight={1.3} />
      )}
    </Container>
  );
};

export default BudgetTable;
